import type { PulseEventContext } from '@cineamo/pulse';

type PurchaseBasket = {
    showingId: number;
    cinemaId: number;
    /** Seat-lock cart lines, one per ticket. */
    seatQuantities: number[];
    /** Cart total in cents, as shown to the guest. */
    total: number;
    concessionQuantities: number[];
};

/** Context of `seat_select` and `concession_view`; cart ids deliberately stay out of it. */
export function buildFunnelContext(showingId: number, cinemaId: number, ticketCount?: number): PulseEventContext {
    return {
        showing_id: showingId,
        cinema_id: cinemaId,
        ...(ticketCount === undefined ? {} : { ticket_count: ticketCount })
    };
}

/** `concessions` is `[]` for a purchase without snacks, which is what Concession Attach counts against. */
export function buildPurchaseContext(basket: PurchaseBasket): PulseEventContext {
    return {
        showing_id: basket.showingId,
        cinema_id: basket.cinemaId,
        ticket_count: basket.seatQuantities.reduce((sum, quantity) => sum + quantity, 0),
        revenue: basket.total,
        concessions: basket.concessionQuantities.map((quantity) => ({ quantity }))
    };
}

const snapshotKey = (cartUuid: string) => `cineamo-pulse-purchase-${cartUuid}`;

/**
 * Taken when the payment starts: a redirect back (PayPal, Klarna, 3DS, Wero) loads a page whose cart is already
 * paid and swapped for an empty one, so the basket is gone by the time the purchase is confirmed.
 */
export function savePurchaseSnapshot(cartUuid: string, context: PulseEventContext): void {
    try {
        sessionStorage.setItem(snapshotKey(cartUuid), JSON.stringify(context));
    } catch {
        // Storage blocked: the purchase is then tracked without basket figures.
    }
}

export function readPurchaseSnapshot(cartUuid: string): PulseEventContext | null {
    try {
        const raw = sessionStorage.getItem(snapshotKey(cartUuid));
        return raw ? (JSON.parse(raw) as PulseEventContext) : null;
    } catch {
        return null;
    }
}

export function clearPurchaseSnapshot(cartUuid: string): void {
    try {
        sessionStorage.removeItem(snapshotKey(cartUuid));
    } catch {
        // Nothing to clean up when storage is blocked.
    }
}

/**
 * Whether `ticket_purchase` may fire. A `succeeded` redirect is paid. A `pending` one (Wero and other bank-backed
 * methods) is not yet: the bank may still refuse it, so only the order reaching `fulfilled` counts, and the success
 * view shown meanwhile is not a purchase. Without a redirect, a paid cart in the session is the purchase.
 */
export function isConfirmedPurchase(purchase: {
    redirectStatus: string | null | undefined;
    isPaidInSession: boolean;
    isRedirectedOrderFulfilled: boolean;
}): boolean {
    if (purchase.redirectStatus === 'succeeded') return true;
    if (purchase.redirectStatus === 'pending') return purchase.isRedirectedOrderFulfilled;
    return purchase.isPaidInSession;
}

const trackedKey = (purchaseId: string) => `cineamo-pulse-purchase-tracked-${purchaseId}`;

/** The success URL survives a reload, so the per-mount ref alone would count the purchase again. */
export function wasPurchaseTracked(purchaseId: string): boolean {
    try {
        return sessionStorage.getItem(trackedKey(purchaseId)) !== null;
    } catch {
        return false;
    }
}

export function markPurchaseTracked(purchaseId: string): void {
    try {
        sessionStorage.setItem(trackedKey(purchaseId), '1');
    } catch {
        // Storage blocked: a reload may count the purchase twice, nothing better to do.
    }
}

/**
 * The `ticket_purchase` context: the live basket when the paid cart is the current one and holds seats, else the
 * snapshot taken at payment, else just the showing and cinema. Never nothing: the purchase happened.
 */
export function resolvePurchaseContext(
    showingId: number,
    cinemaId: number,
    liveBasket: PurchaseBasket | null,
    snapshot: PulseEventContext | null
): PulseEventContext {
    if (liveBasket && liveBasket.seatQuantities.length > 0) return buildPurchaseContext(liveBasket);
    return snapshot ?? buildFunnelContext(showingId, cinemaId);
}
